import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Scissors, Filter, Loader2, RefreshCw, Download, Eye, AlertCircle,
  CheckCircle2, Clock, XCircle, PlayCircle,
} from "lucide-react";
import { cn } from "@/lib/util";
import { plain } from "@/lib/plain";
import { usePermissions } from "@/lib/permissions";

const STATUSES = [
  { id: "requested",   label: "Solicitado",   icon: Clock,        color: "text-amber-400 border-amber-400/40 bg-amber-400/10" },
  { id: "in_progress", label: "En progreso",  icon: PlayCircle,   color: "text-blue-400 border-blue-400/40 bg-blue-400/10" },
  { id: "completed",   label: "Completado",   icon: CheckCircle2, color: "text-emerald-400 border-emerald-400/40 bg-emerald-400/10" },
  { id: "rejected",    label: "Rechazado",    icon: XCircle,      color: "text-rose-400 border-rose-400/40 bg-rose-400/10" },
];

const statusMeta = (id) => STATUSES.find(s => s.id === id) || STATUSES[0];
const fmt = (iso) => iso ? new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }) : "—";

export default function Takedowns() {
  const { isClient } = usePermissions();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [selected, setSelected] = useState(null);
  const [draftNotes, setDraftNotes] = useState("");
  const [draftStatus, setDraftStatus] = useState("");
  const [draftClientNote, setDraftClientNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterClient) params.client_id = filterClient;
      if (filterFrom) params.since = filterFrom + "T00:00:00Z";
      if (filterTo) params.until = filterTo + "T23:59:59Z";
      const [list, summary] = await Promise.all([
        api.get("/takedowns", { params }).then(r => r.data),
        api.get("/takedowns/stats/summary").then(r => r.data),
      ]);
      setRows(list);
      setStats(summary);
    } catch (e) {
      toast.error("No se pudo cargar la lista de takedowns");
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterClient, filterFrom, filterTo]);

  useEffect(() => {
    if (isClient) return;
    api.get("/clients").then(r => setClients(r.data || [])).catch(() => {});
  }, [isClient]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try {
      const r = await api.get(`/takedowns/${id}`);
      setSelected(r.data);
      setDraftNotes(r.data.notes || "");
      setDraftClientNote(r.data.client_note || "");
      setDraftStatus(r.data.status || "requested");
    } catch (e) { toast.error("No se pudo abrir el takedown"); }
  };

  const saveTakedown = async () => {
    if (!selected) return;
    try {
      const payload = {};
      if (draftStatus !== selected.status) payload.status = draftStatus;
      if (draftNotes !== (selected.notes || "")) payload.notes = draftNotes;
      if (draftClientNote !== (selected.client_note || "")) payload.client_note = draftClientNote;
      if (!Object.keys(payload).length) {
        toast.info("Nada que guardar");
        return;
      }
      const r = await api.patch(`/takedowns/${selected.id}`, payload);
      toast.success("Takedown actualizado");
      const n = r.data.notification;
      if (n) {
        if (n.dispatched) toast.success(`Aviso enviado a ${n.recipients} usuario(s) del cliente`);
        else toast.warning(n.reason === "smtp_not_configured"
          ? "Aviso al cliente no enviado: email (SMTP) no configurado"
          : `Aviso al cliente no enviado (${n.reason || "sin destinatarios"})`);
      }
      openDetail(selected.id);
      load();
    } catch (e) {
      toast.error("Falló la actualización");
    }
  };

  const downloadPdf = async (id, target) => {
    try {
      const r = await api.get(`/takedowns/${id}/evidence.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Takedown-${(target || id).slice(0, 40)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo descargar el PDF"); }
  };

  return (
    <div className="space-y-6" data-testid="takedowns-page">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-medium flex items-center gap-2">
            <Scissors className="w-5 h-5 text-primary" />
            Takedowns
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Gestión de retiradas de activos maliciosos — multi-tenant con evidencia PDF.
          </p>
        </div>
        <button onClick={load} disabled={loading}
                className="border border-border px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase inline-flex items-center gap-2 hover:border-primary hover:text-primary disabled:opacity-50"
                data-testid="takedowns-refresh-btn">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refrescar
        </button>
      </header>

      {/* ---- stats row ---- */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Total" value={stats.total} />
          {STATUSES.map(s => (
            <StatCard key={s.id} label={s.label} value={stats.by_status?.[s.id] || 0}
                       icon={s.icon} color={s.color} />
          ))}
        </div>
      )}

      {/* ---- filters ---- */}
      <div className="border border-border bg-card/70 p-4 flex flex-wrap items-end gap-3">
        <div className="inline-flex items-center gap-1 text-xs font-mono text-muted-foreground">
          <Filter className="w-3 h-3" /> Filtros
        </div>
        <FilterSelect label="Estado" value={filterStatus} onChange={setFilterStatus}
                       options={[["", "Todos"], ...STATUSES.map(s => [s.id, s.label])]}
                       testid="filter-status" />
        {!isClient && (
          <FilterSelect label="Cliente" value={filterClient} onChange={setFilterClient}
                         options={[["", "Todos"],
                                   ...clients.map(c => [c.id, plain(c.name, `#${c.id.slice(0,8)}`)])]}
                         testid="filter-client" />
        )}
        <FilterDate label="Desde" value={filterFrom} onChange={setFilterFrom} testid="filter-from" />
        <FilterDate label="Hasta" value={filterTo} onChange={setFilterTo} testid="filter-to" />
      </div>

      {/* ---- table ---- */}
      <div className="border border-border bg-card/70" data-testid="takedowns-table">
        <table className="w-full font-mono text-xs">
          <thead className="border-b border-border bg-background/50">
            <tr className="text-left text-muted-foreground uppercase tracking-widest">
              <th className="p-3">Activo</th>
              <th className="p-3">Cliente</th>
              <th className="p-3">Prioridad</th>
              <th className="p-3">Estado</th>
              <th className="p-3">Creado</th>
              <th className="p-3">Actualizado</th>
              <th className="p-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">
                Sin takedowns en los filtros actuales.
              </td></tr>
            )}
            {rows.map(r => {
              const sm = statusMeta(r.status);
              const SI = sm.icon;
              return (
                <tr key={r.id} className="border-t border-border hover:bg-background/40"
                    data-testid={`takedown-row-${r.id}`}>
                  <td className="p-3 font-sans"><div className="font-medium">{r.target}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{r.evidence?.category || "—"}</div></td>
                  <td className="p-3 font-sans">{plain(r.client_name) || "—"}</td>
                  <td className="p-3 uppercase">{r.priority || "medium"}</td>
                  <td className="p-3">
                    <span className={cn("inline-flex items-center gap-1 border px-2 py-0.5", sm.color)}>
                      <SI className="w-3 h-3" />{sm.label}
                    </span>
                  </td>
                  <td className="p-3 text-muted-foreground">{fmt(r.created_at)}</td>
                  <td className="p-3 text-muted-foreground">{fmt(r.updated_at)}</td>
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center gap-2 justify-end">
                      <button onClick={() => openDetail(r.id)}
                              className="border border-border px-2 py-1 inline-flex items-center gap-1 hover:border-primary hover:text-primary"
                              data-testid={`takedown-view-${r.id}`}>
                        <Eye className="w-3 h-3" />Detalle
                      </button>
                      <button onClick={() => downloadPdf(r.id, r.target)}
                              className="border border-border px-2 py-1 inline-flex items-center gap-1 hover:border-primary hover:text-primary"
                              data-testid={`takedown-pdf-${r.id}`}>
                        <Download className="w-3 h-3" />PDF
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ---- detail drawer ---- */}
      {selected && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-4"
             onClick={() => setSelected(null)} data-testid="takedown-drawer">
          <div onClick={e => e.stopPropagation()}
                className="bg-card border border-border w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div>
                <h2 className="font-display text-lg font-medium flex items-center gap-2">
                  <Scissors className="w-4 h-4 text-primary" />
                  {selected.target}
                </h2>
                <div className="text-xs text-muted-foreground font-mono mt-1">
                  {plain(selected.client_name)} · {selected.id.slice(0,8)}
                </div>
              </div>
              <button onClick={() => setSelected(null)}
                      className="text-muted-foreground hover:text-foreground p-1">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <KV k="Finding ref" v={<Link to={`/findings/${selected.finding_id}`}
                                              className="text-primary hover:underline">
                                        {selected.finding_id?.slice(0,8)}…
                                      </Link>} />
                <KV k="Prioridad" v={(selected.priority || "medium").toUpperCase()} />
                <KV k="Solicitado por" v={selected.requested_by_email || "—"} />
                <KV k="Creado" v={fmt(selected.created_at)} />
                <KV k="Completado" v={fmt(selected.completed_at)} />
                <KV k="Rechazado" v={fmt(selected.rejected_at)} />
              </div>

              <div className="border-t border-border pt-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                  Evidencia (autopoblada)
                </div>
                <div className="space-y-2">
                  <div><span className="text-muted-foreground">Título:</span> {selected.evidence?.title || "—"}</div>
                  <div><span className="text-muted-foreground">Severidad:</span> <span className="uppercase font-mono">{selected.evidence?.severity}</span></div>
                  <div><span className="text-muted-foreground">Descripción:</span>
                    <p className="text-sm text-foreground/80 mt-1">{selected.evidence?.description || "—"}</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-border pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Estado
                  </label>
                  {isClient ? (
                    <div className="mt-1 text-sm" data-testid="takedown-detail-status-readonly">
                      {statusMeta(selected.status).label}
                    </div>
                  ) : (
                    <select value={draftStatus} onChange={e => setDraftStatus(e.target.value)}
                            className="w-full mt-1 bg-background border border-border px-3 py-2 text-sm focus:border-primary outline-none"
                            data-testid="takedown-detail-status">
                      {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                  )}
                </div>
                <div className="flex items-end justify-end">
                  <button onClick={() => downloadPdf(selected.id, selected.target)}
                          className="border border-border px-3 py-2 inline-flex items-center gap-2 text-sm hover:border-primary hover:text-primary"
                          data-testid="takedown-detail-pdf">
                    <Download className="w-3 h-3" /> Descargar evidencia PDF
                  </button>
                </div>
              </div>

              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Nota para el cliente
                </label>
                {isClient ? (
                  <p className="mt-1 text-sm whitespace-pre-wrap text-foreground/90" data-testid="takedown-detail-client-note-readonly">
                    {selected.client_note || "—"}
                  </p>
                ) : (
                  <textarea value={draftClientNote} onChange={e => setDraftClientNote(e.target.value)}
                             rows={3} maxLength={4000}
                             placeholder="Visible para el cliente y se incluye en el aviso por email al pasar a En progreso, Completado o Rechazado."
                             className="w-full mt-1 bg-background border border-border px-3 py-2 text-sm focus:border-primary outline-none"
                             data-testid="takedown-detail-client-note" />
                )}
              </div>

              {!isClient && <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Notas internas del analista (no visibles para el cliente)
                </label>
                <textarea value={draftNotes} onChange={e => setDraftNotes(e.target.value)}
                           rows={5}
                           placeholder="Anota aquí comunicaciones con el registrador, hosting, evidencia complementaria, etc."
                           className="w-full mt-1 bg-background border border-border px-3 py-2 text-sm focus:border-primary outline-none font-mono"
                           data-testid="takedown-detail-notes" />
              </div>}

              {selected.events && selected.events.length > 0 && (
                <div className="border-t border-border pt-4">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                    Historial
                  </div>
                  <div className="space-y-1 text-xs text-muted-foreground max-h-32 overflow-y-auto font-mono">
                    {selected.events.map(ev => (
                      <div key={ev.id}>· {fmt(ev.created_at)} · {ev.action} · {ev.actor_email}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-5 border-t border-border flex justify-end gap-2">
              <button onClick={() => setSelected(null)}
                      className="border border-border px-4 py-2 text-sm hover:border-muted-foreground">
                Cerrar
              </button>
              {!isClient && (
                <button onClick={saveTakedown}
                        className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:brightness-110"
                        data-testid="takedown-detail-save">
                  Guardar cambios
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className={cn("border border-border bg-card/70 p-3", color)}>
      <div className="text-[10px] uppercase tracking-widest flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className="font-display text-2xl mt-1 text-foreground">{value || 0}</div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
              className="bg-background border border-border px-3 py-1.5 text-sm focus:border-primary outline-none"
              data-testid={testid}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function FilterDate({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{label}</label>
      <input type="date" value={value} onChange={e => onChange(e.target.value)}
             className="bg-background border border-border px-3 py-1.5 text-sm focus:border-primary outline-none font-mono"
             data-testid={testid} />
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{k}</div>
      <div className="text-sm mt-0.5">{v || "—"}</div>
    </div>
  );
}
